// api.ts — the one place that talks to thin-router.
//
// Two rules this module exists to enforce:
//   1. A FAILED LOAD IS REPORTED, NEVER SUBSTITUTED (TD-034). There is deliberately no fallback
//      sample data anywhere in this file. A tender list that renders empty because a fetch failed
//      tells an officer there are no tenders — in this product that is a lie with consequences.
//   2. The bearer token is attached here and nowhere else, so there is one place to audit.

let bearer: string | null = null

export function setBearer(token: string | null) {
  bearer = token
}

// ── Dev sign-in ───────────────────────────────────────────────────────────────────────────────
// With auth bypassed there is no login, so switching user used to mean editing .env and recreating
// a container. This header names the identity the request wants. The SERVER honours it only while
// TOWNFORGE_AUTH_BYPASS is true; with real auth on it is ignored entirely, so it can never become
// an impersonation hole. Persisted so a page reload keeps you signed in as the same person.
const DEV_USER_KEY = 'tf.devUser'

export function devUser(): string | null {
  try { return localStorage.getItem(DEV_USER_KEY) } catch { return null }
}

export function setDevUser(id: string | null) {
  try {
    if (id) localStorage.setItem(DEV_USER_KEY, id)
    else localStorage.removeItem(DEV_USER_KEY)
  } catch { /* private mode: sign-in lasts for this page only, which is acceptable */ }
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly detail: string) {
    super(`${status}: ${detail}`)
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  if (bearer) headers.set('Authorization', `Bearer ${bearer}`)
  const dev = devUser()
  if (dev) headers.set('X-TownForge-Dev-User', dev)

  const res = await fetch(`/api/townforge${path}`, { ...init, headers })
  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = await res.json()
      detail = body?.detail ?? detail
    } catch { /* the body was not JSON; the status text is what we have */ }
    throw new ApiError(res.status, detail)
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T)
}

export interface Me {
  user: { id: string; email: string; name: string; isSuperuser: boolean }
  org: { id: string; name: string; orgType: string; mode: string } | null
  role: string | null
  designation: string | null
  entitlements: { features: string[] }
  authEnabled: boolean
}

export interface Tender {
  id: string
  orgId: string
  tenderRef: string
  title: string
  tenderTypeKey: string
  category: string
  procurementMethod: string
  division: string | null
  location: string | null
  contractPeriodMonths: number | null
  estimatedValue: number | null
  status: string
  advertisedAt: string | null
  opensAt: string | null
  closesAt: string | null
  validityDays: number
  evalModel: string
  priceDirection: 'lowest' | 'highest'
  contingencyPct: number
  mgmtFeePct: number
  createdAt: string
  contractStartDate?: string | null
  contractEndDate?: string | null
  equipment?: Array<{ class: string; location: string | null; count: number | null }>
}

/** The tender box BEFORE opening: counts, identities, arrival times. Never content, never a sum. */
export interface TenderBox {
  tenderStatus: string
  closesAt: string | null
  /** Always use this, never the browser clock, to compute remaining time (TD-010). */
  serverNow: string
  opened: boolean
  registered: number
  submitted: number
  notSubmitted: string[]
  submissions: Array<{
    id: string
    supplierOrgId: string
    submittedAt: string
    admissible: boolean
    status: string
    sha256: string
    sealed: boolean
  }>
}

/** Everything one tender page needs, composed server-side in a single round trip.
 *
 *  `sealed` and the NULL sums before opening are not a UI preference — the server does not send
 *  them (Law 2, enforced in townforge_tenders.py). The types say `| null` because that is the
 *  honest shape of the data, and a screen that renders a seal is rendering the truth. */
export interface TenderOverview {
  tender: Tender
  opened: boolean
  /** Legal next lifecycle states, decided by the server (workflow-state-model.md §1). */
  nextStates: string[]
  registrations: Array<{ supplierOrgId: string; name: string | null; registeredAt: string | null }>
  submissions: Array<{
    id: string
    supplierOrgId: string
    supplierName: string | null
    submittedAt: string | null
    status: string
    admissible: boolean | null
    sha256: string
    sealed: boolean
    tenderSum: number | null
    responsive: boolean | null
    responsiveReason: string | null
    responsiveAuto: boolean | null
  }>
  matrix: {
    version: number
    evalModel: string
    lockedAt: string | null
    criteria: Array<{
      id: string; areaKey: string; areaName: string; critKey: string
      title: string; weightPct: number; maxScore: number | null
    }>
  } | null
  assignments: Array<{
    id: string; evaluatorName: string | null; areas: string[]
    role: string | null; status: string | null; submittedAt: string | null
  }>
  opening: {
    startedAt: string | null; completedAt: string | null
    officerA: string | null; officerB: string | null
    recordSha256: string | null
    lines: Array<{ supplierName: string | null; tenderSum: number | null; remark: string | null }>
  } | null
  scores: Array<{
    submissionId: string; critKey: string; critTitle: string
    score: number; comment: string | null
    disposition: string | null; changeReason: string | null
    scorerName: string | null; scoredAt: string | null
    aiProposedScore: number | null; aiRationale: string | null
    aiCitations: Array<{ doc?: string; page?: number; quote?: string }> | null
  }>
  probityFindings: Array<{
    kind: string; severity: string | null; subjectOrgIds: string[]
    detail: { summary?: string } | null
    disposition: string | null; dispositionReason: string | null
  }>
  coi: Array<{
    userName: string | null; roleContext: string; hasConflict: boolean
    disposition: string | null; dispositionReason: string | null
  }>
}

export interface TenderType {
  key: string
  name: string
  category: string
  description: string | null
  evalModel: string
  priceDirection: string
  requirementCount: number
  criterionCount: number
  /** False = calling it would produce a tender with no declared submission format. */
  hasTemplate: boolean
}

// ── Arrears Management ────────────────────────────────────────────────────────────────────────
export interface ArrearsCase {
  id: string; accountNo: string; residentName: string; address: string; division: string
  status: string; category: string | null; hardshipCode: string | null
  assignedRole: string | null; assignedUserId: string | null
  priorityScore: number
  /** Says WHICH of the two orderings produced the score — 'urgency' for hardship, otherwise
   *  'recoverability'. Rendered on screen, because an officer must be able to say why this
   *  resident was contacted. */
  priorityBasis: { basis: string; reason?: string; [k: string]: unknown } | null
  nextAction: string | null; nextActionDue: string | null
  balanceCents: number | null; daysOverdue: number | null; bucket: string
  monthsOfCharge: number
  /** When TCMS was last read. Shown as staleness — never hidden. */
  figuresAsOf: string | null
  evidenceExpiresOn: string | null
}

export interface AmsOverview {
  division: string | null
  totalAccounts: number; accountsInArrears: number; arrearsCents: number
  buckets: Array<{ bucket: string; count: number; cents: number }>
  categories: Array<{ key: string; count: number }>
  hardshipDrivers: Array<{ code: string; count: number }>
  divisions: Array<{ division: string; count: number; d90: number }>
  /** The MND measure WITH its denominator, so the percentage can never be quoted without it. */
  mndBasis: { measure: string; numerator: number; denominator: number; pct: number
              greenBandPct: number; targetPct: number }
  attention: { approvals_waiting: number; promises_broken: number
               evidence_expiring: number; sends_unconfirmed: number }
  figuresAsOf: string | null
}

export interface HardshipRule {
  code: string; driver: string; treatment: string; offer: string; evidence: string
  tier: 'plan_conditional' | 'deferral' | 'hard_stop'
  blocksDemand: boolean; reviewMonths: number | null
}

export interface AmsApproval {
  id: string; caseId: string; stageKey: string; stageName: string; feeCents: number
  lawFirm: boolean; approverRole: string; recommendedAt: string
  /** Who recommended this letter — the id, and the NAME. An approver is being asked to take
   *  responsibility for someone else's judgement, and a user id does not tell them whose. */
  recommendedBy: string; recommendedByName: string | null
  accountNo: string; residentName: string; division: string
  category: string | null; hardshipCode: string | null
  balanceCents: number | null; daysOverdue: number | null
  /** Computed server-side. The server refuses anyway; this is so the UI never offers the button. */
  blocked: boolean; blockedReason: string | null
}

export interface AmsVisit {
  id: string; caseId: string; assignedRole: string; scheduledFor: string | null
  status: string; outcome: string | null
  accountNo: string; residentName: string; address: string; division: string
  category: string | null; hardshipCode: string | null
  balanceCents: number | null; daysOverdue: number | null; monthsOfCharge: number
  /** Why this door — the system's own next action for the case, not a restated category. */
  nextAction: string | null
}

export interface DevUser {
  id: string; name: string; email: string; isSuperuser: boolean
  role: string | null; designation: string | null
  orgId: string | null; orgName: string | null; orgType: string | null
}

export const api = {
  me: () => request<Me>('/me'),
  devUsers: () => request<{ users: DevUser[] }>('/dev/users'),
  health: () => request<{ status: string; db: string }>('/health'),
  tenderTypes: () => request<{ types: TenderType[] }>('/tender-types'),

  tenders: {
    list: (orgId: string) => request<{ tenders: Tender[] }>(`/tenders/${orgId}`),
    get: (orgId: string, id: string) => request<Tender>(`/tenders/${orgId}/${id}`),
    overview: (orgId: string, id: string) =>
      request<TenderOverview>(`/tenders/${orgId}/${id}/overview`),
    create: (orgId: string, body: Record<string, unknown>) =>
      request<Tender>(`/tenders/${orgId}`, { method: 'POST', body: JSON.stringify(body) }),
    update: (orgId: string, id: string, body: Record<string, unknown>) =>
      request<Tender>(`/tenders/${orgId}/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    transition: (orgId: string, id: string, to: string, reason?: string) =>
      request<Tender>(`/tenders/${orgId}/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ to, reason }),
      }),
  },

  ams: {
    overview: (org: string, division?: string) =>
      request<AmsOverview>(`/ams/${org}/overview${division ? `?division=${division}` : ''}`),
    cases: (org: string, q: Record<string, string | undefined> = {}) => {
      const p = new URLSearchParams()
      Object.entries(q).forEach(([k, v]) => { if (v) p.set(k, v) })
      return request<{ cases: ArrearsCase[]; count: number }>(`/ams/${org}/cases?${p}`)
    },
    case: (org: string, id: string) => request<{
      case: ArrearsCase
      live: { balanceCents: number; daysOverdue: number; monthsOfCharge: number; asOf: string }
      /** Held by AMS, not by the finance system — AMS is the only thing that triggers a send. */
      suppression: { tier: string; hardshipCode: string | null; suppressUntil: string | null
                     feeBearingOnly: boolean; reason: string | null
                     setBy: string; setAt: string } | null
      ledger: Array<{ postedOn: string; kind: string; amountCents: number; description: string }>
      rule: HardshipRule | null
      events: Array<{ id: string; at: string; kind: string; channel: string | null
                      summary: string; detail: Record<string, unknown> | null
                      /** The role, and the person. Null when nothing human did it. */
                      actorRole: string | null; actorName: string | null }>
      visits: Array<{ id: string; assignedRole: string; scheduledFor: string | null
                      status: string; outcome: string | null; hardshipCode: string | null
                      notes: string | null }>
      promises: Array<{ id: string; promisedOn: string; amountCents: number; status: string }>
      approvals: Array<{ id: string; stageKey: string; status: string; approverRole: string
                         recommendedAt: string; blockedReason: string | null }>
    }>(`/ams/${org}/cases/${id}`),
    setCategory: (org: string, id: string, body: Record<string, unknown>) =>
      request<{ caseId: string; category: string; hardshipCode: string | null
                suppressionApplied: { tier: string; until: string | null } | null }>(
        `/ams/${org}/cases/${id}/category`, { method: 'POST', body: JSON.stringify(body) }),
    approvals: (org: string) => request<{ approvals: AmsApproval[] }>(`/ams/${org}/approvals`),
    decide: (org: string, id: string, decision: 'approve' | 'return', reason?: string) =>
      request<{ approvalId: string; status: string; send?: { status: string }
                balanceAtApproval?: number; verifiedAsOf?: string }>(
        `/ams/${org}/approvals/${id}/decide`,
        { method: 'POST', body: JSON.stringify({ decision, reason }) }),
    rules: (org: string) => request<{
      rules: HardshipRule[]
      stages: Array<{ key: string; name: string; order: number; approverRole: string
                      feeCents: number; lawFirm: boolean }>
    }>(`/ams/${org}/rules`),
    /** `oversees` is true for a manager, who sees the whole round rather than their own. */
    visits: (org: string) =>
      request<{ oversees: boolean; visits: AmsVisit[] }>(`/ams/${org}/visits`),
    outreach: (org: string) => request<{
      eligibleAccounts: number
      channels: Array<{ channel: string; sent: number; delivered: number; read: number
                        unconfirmed: number }>
      promises: { total: number; kept: number; broken: number; open: number }
      rules: Array<{ risk: string; day: number; channel: string; message: string
                     ifNoResponse: string }>
    }>(`/ams/${org}/outreach`),
    legal: (org: string) => request<{
      /** AMS's recorded fee against the fee TCMS actually posted, per letter. Deliberately NOT a
       *  law-firm invoice check: we hold no invoice data, and claiming to have checked one would
       *  be inventing the most consequential fact on the page. */
      feeCheck: { checked: number; differenceCount: number; scope: string
                  differences: Array<{ letterId: string; caseId: string; residentName: string
                                       accountNo: string; issuedOn: string
                                       amsFeeCents: number; tcmsFeeCents: number
                                       deltaCents: number }> }
      stages: Array<{ stageKey: string; stageName: string; issued: number; feesCents: number
                      paidSinceCents: number; notNotified: number }>
      letters: Array<{ id: string; caseId: string; stageKey: string; stageName: string
                       issuedAt: string; feeCents: number; lawFirmNotifiedAt: string | null
                       paymentSinceCents: number; accountNo: string; residentName: string
                       division: string; balanceCents: number; daysOverdue: number }>
    }>(`/ams/${org}/legal`),
    reports: (org: string) => request<{
      catalog: Array<{ n: number; title: string; status: string }>
      transition: { fromDate: string | null; toDate: string | null
                    cells: Array<{ from_bucket: string; to_bucket: string; n: number }> }
      ageing: Array<{ bucket: string; division: string; count: number; cents: number }>
      workflow: Record<string, number>
      promises: Array<{ status: string; count: number }>
    }>(`/ams/${org}/reports`),
    /** `effects` is what the server REPORTS IT DID — the confirmation screen renders only these,
     *  so it can never claim an action the system did not take. */
    logVisit: (org: string, id: string, body: Record<string, unknown>) =>
      request<{
        visitId: string; resident: string; address: string
        effects: Array<{ kind: string; summary: string; detail: string | null }>
        next: { visitId: string; address: string; residentName: string } | null
      }>(`/ams/${org}/visits/${id}/log`, { method: 'POST', body: JSON.stringify(body) }),

    /** Today's calls, ranked — with the households the rules protect removed, and a count of how
     *  many were removed. A silent exclusion is its own kind of failure. */
    callQueue: (org: string, limit = 40) =>
      request<{
        cases: ArrearsCase[]
        excluded: { protectedByRule: number; underAssessment: number; rules: string[] }
      }>(`/ams/${org}/call-queue?limit=${limit}`),

    /** Name, account number or address, all at once — a caller says whichever they have. */
    search: (org: string, q: string) =>
      request<{ query: string; cases: ArrearsCase[] }>(
        `/ams/${org}/search?q=${encodeURIComponent(q)}`),

    /** The CLT's hardship assessment. The only route into Category 1. */
    logAssessment: (org: string, caseId: string, body: Record<string, unknown>) =>
      request<{
        caseId: string; resident: string
        effects: Array<{ kind: string; summary: string; detail: string | null }>
      }>(`/ams/${org}/cases/${caseId}/assessment`, { method: 'POST', body: JSON.stringify(body) }),

    /** The call centre's only write. Same contract as logVisit: `effects` is what the server
     *  reports it DID, so the screen can never claim an action the system did not take. */
    logContact: (org: string, caseId: string, body: Record<string, unknown>) =>
      request<{
        caseId: string; resident: string
        effects: Array<{ kind: string; summary: string; detail: string | null }>
      }>(`/ams/${org}/cases/${caseId}/contact`, { method: 'POST', body: JSON.stringify(body) }),
  },

  submissions: {
    box: (tcOrgId: string, tenderId: string) =>
      request<TenderBox>(`/submissions/${tcOrgId}/box/${tenderId}`),
    register: (supplierOrgId: string, body: Record<string, unknown>) =>
      request<{ status: string }>(`/submissions/${supplierOrgId}/register`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    submit: (supplierOrgId: string, body: Record<string, unknown>) =>
      request<{
        submissionId: string
        receivedAt: string
        admissible: boolean
        reason: string | null
        sha256: string
        note: string
      }>(`/submissions/${supplierOrgId}/submit`, { method: 'POST', body: JSON.stringify(body) }),
  },
}
